import { RotateCcw } from "lucide-react";
import { Segmented } from "@/components/ui/segmented";
import { LEVEL_LABELS } from "@/lib/level-badge";
import { cn } from "@/lib/utils";
import { HAZARD_LABELS, HORIZON_LABELS } from "@/lib/results";
import { SCENARIO_OPTIONS, defaultResultsFilterState, type ResultsFilterState } from "@/lib/results-filters";
import { HORIZONS } from "@shared/hazard-horizons";
import type { Asset, ValueChainStage } from "@/lib/assets";
import type { HazardType, TimeHorizon } from "@shared/scoring/types";

/** Uppercase eyebrow used by every filter group in the bar. */
function FilterLabel({ children }: { children: string }) {
  return (
    <span className="text-xs font-semibold uppercase leading-4 tracking-[0.08em] text-muted-foreground">
      {children}
    </span>
  );
}

interface ChipToggleGroupProps {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}

function ChipToggleGroup({ label, options, selected, onChange }: ChipToggleGroupProps) {
  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }

  if (options.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterLabel>{label}</FilterLabel>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const isOn = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
                isOn
                  ? "border-primary bg-primary/10 font-bold text-primary"
                  : "border-input text-muted-foreground hover:border-primary/50"
              )}
            >
              <span className={cn("size-1.5 rounded-full", isOn ? "bg-primary" : "bg-muted-foreground/40")} />
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ResultsFiltersProps {
  filters: ResultsFilterState;
  onChange: (filters: ResultsFilterState) => void;
  assets: Asset[];
  stages: ValueChainStage[];
}

export function ResultsFilters({ filters, onChange, assets, stages }: ResultsFiltersProps) {
  const countries = Array.from(new Set(assets.map((a) => a.country))).sort();
  const cities = Array.from(new Set(assets.map((a) => a.city))).sort();

  function set<K extends keyof ResultsFilterState>(key: K, value: ResultsFilterState[K]) {
    onChange({ ...filters, [key]: value });
  }

  return (
    <div className="-mx-6 -mt-6 mb-0 flex flex-col gap-3 border-b border-border bg-card px-6 py-3 md:-mx-8 md:px-8">
      {/* Primary row — the three the redesign puts front and centre. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <FilterLabel>Scenario</FilterLabel>
          <Segmented
            options={SCENARIO_OPTIONS}
            isActive={(v) => filters.scenario === v}
            onToggle={(v) => set("scenario", v as ResultsFilterState["scenario"])}
          />
        </div>

        <div className="flex items-center gap-2">
          <FilterLabel>Horizon</FilterLabel>
          <Segmented
            options={HORIZONS.map((h) => ({ value: h, label: HORIZON_LABELS[h] }))}
            isActive={(v) => filters.horizons.includes(v as TimeHorizon)}
            onToggle={(v) => {
              const horizon = v as TimeHorizon;
              const next = filters.horizons.includes(horizon)
                ? filters.horizons.filter((h) => h !== horizon)
                : [...filters.horizons, horizon];
              // At least one horizon must always stay selected -- an empty
              // selection has nothing for HorizonComparisonGrid to render, so
              // it would blank the whole results page rather than mean "no
              // filter."
              if (next.length === 0) return;
              set("horizons", next);
            }}
          />
        </div>

        <ChipToggleGroup
          label="Hazards"
          options={(Object.keys(HAZARD_LABELS) as HazardType[]).map((h) => ({ value: h, label: HAZARD_LABELS[h] }))}
          selected={filters.hazards}
          onChange={(v) => set("hazards", v as HazardType[])}
        />

        <button
          type="button"
          onClick={() => onChange(defaultResultsFilterState())}
          className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <RotateCcw className="h-3 w-3" />
          Reset Filters
        </button>
      </div>

      {/* The spec requires all nine filters "each shown separately, not
          bundled", so the remaining six stay visible here rather than moving
          behind a "More filters" disclosure the redesign implies. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-border pt-3">
        <ChipToggleGroup
          label="Countries"
          options={countries.map((c) => ({ value: c, label: c }))}
          selected={filters.countries}
          onChange={(v) => set("countries", v)}
        />
        <ChipToggleGroup
          label="Cities"
          options={cities.map((c) => ({ value: c, label: c }))}
          selected={filters.cities}
          onChange={(v) => set("cities", v)}
        />
        <ChipToggleGroup
          label="Facilities"
          options={assets.map((a) => ({ value: a.id, label: a.name }))}
          selected={filters.facilities}
          onChange={(v) => set("facilities", v)}
        />
        <ChipToggleGroup
          label="Asset importance"
          options={[1, 2, 3, 4].map((n) => ({ value: String(n), label: LEVEL_LABELS[n as 1 | 2 | 3 | 4] }))}
          selected={filters.assetImportance.map(String)}
          onChange={(v) => set("assetImportance", v.map(Number) as Array<1 | 2 | 3 | 4>)}
        />
        <ChipToggleGroup
          label="Value chain stage"
          options={stages.map((s) => ({ value: s.id, label: s.name }))}
          selected={filters.valueChainStageIds}
          onChange={(v) => set("valueChainStageIds", v)}
        />
        <ChipToggleGroup
          label="Value chain importance"
          options={[0, 1, 2, 3, 4].map((n) => ({ value: String(n), label: String(n) }))}
          selected={filters.valueChainImportance.map(String)}
          onChange={(v) => set("valueChainImportance", v.map(Number) as Array<0 | 1 | 2 | 3 | 4>)}
        />
      </div>
    </div>
  );
}
